import React, { useEffect, useState, useRef } from "react";
import { useMutation, useLazyQuery } from "@apollo/client";
import { DELETE_BA_GROUP } from "../../../../utils/GraphQL/mutations";
import { GET_ONE_BAG } from "../../../../utils/GraphQL/queries";
import { baImageUrl } from "../../../../utils/baImageUrl";
import BdrSpinner from "../../../../components/BdrSpinner/BdrSpinner";
import { MyFormHelperText } from "../../../../components/CssTextFields/CssTextFieldStandard";
import BdrInput from "../../../../components/BdrInput/BdrInput";
import nameListFormat from "../../../../utils/formatters/refinisherFormat";
import { useSnackbar } from "../../../../utils/GlobalSnackbar/GlobalSnackbarContext";
import BdrDisclosure from "../../../../components/BdrDisclosure/BdrDisclosure";
import BdrButton from "../../../../components/BdrButton/BdrButton";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import { useMediaQuery } from "@mui/material";
import "./deleteBAGroup.css";

const DeleteBAGroup = ({ inPanel = false }) => {
  const deleteBAGScrollRef = useRef();
  const deleteBagInputRef = useRef();
  const [selectedId, setSelectedId] = useState("");
  const [errorState, setErrorState] = useState(false);
  const smaller900 = useMediaQuery("(max-width:900px)")
  const [bagState, setBAGState] = useState({});
  const [barCollapsed, setBarCollapsed] = useState(true);
  const { handleSnack } = useSnackbar();
  const [deleteBAG, { loading: BAGDeleteLoading }] =
    useMutation(DELETE_BA_GROUP);
  const [queryOneBAG, { loading: BAGQueryLoading }] = useLazyQuery(GET_ONE_BAG);

  useEffect(() => {
    if (selectedId.length === 0) {
      clearError();
      return;
    }

    checkIfQuery();

    return;
    // eslint-disable-next-line
  }, [selectedId]);

  const handleGroupIdChange = (event) => {
    setSelectedId(event.target.value);
  };

  const checkIfQuery = async () => {
    const validId = /^[0-9a-fA-F]{24}$/.test(selectedId);

    if (validId) {
      const { data } = await queryOneBAG({
        variables: { selectedId: selectedId },
      });

      if (data?.getBAGById !== null) {
        setBAGState(data?.getBAGById);
      }

      if (data?.getBAGById === null) {
        setBAGState({});
      }

      return;
    }

    if (validId === false) {
      setBAGState({});
      return;
    }
    return;
  };

  const checkError = async () => {
    //state is for display. return is for "is valid"
    if (selectedId.match(/^[0-9a-fA-F]{24}$/) || selectedId?.length === 0) {
      setErrorState(false);
      return true;
    }
    setErrorState(true);
    return false;
  };

  const clearError = () => {
    setErrorState(false);
  };

  return (
    <div ref={deleteBAGScrollRef}
      id="delete-ba-group-height-marker"
      className={inPanel ? "" : "bdr-v2-admin-container"}
    >
      <div
        className={
          BAGDeleteLoading || BAGQueryLoading
            ? "delete-over-loader"
            : "delete-none"
        }
      >
        <BdrSpinner message={"Loading..."} />
      </div>
      <BdrDisclosure
        bare={inPanel}
        open={!barCollapsed}
        label="Delete BA Group"
        icon={<DeleteForeverIcon />}
        onToggle={() => {
          setTimeout(() => {
            if (smaller900 === true && barCollapsed === true) {
              const navHeight = document.querySelector(
                "#admin-nav-height-marker"
              )?.offsetHeight;
              const pagePos = deleteBAGScrollRef.current.offsetTop;

              const totalMobileScroll = pagePos - navHeight - 5;

              window.scrollTo(0, totalMobileScroll);
              setTimeout(() => {
                deleteBagInputRef.current?.focus()
              }, 800);
            }

            if (smaller900 === false && barCollapsed === true) {
              deleteBAGScrollRef.current.scrollIntoView({block: "start"});
              setTimeout(() => {
                deleteBagInputRef.current?.focus()
              }, 800);
            }

            if ( barCollapsed === false ) {
              window.scrollTo({top: 0});
            }
          }, 500);
          setBarCollapsed((prev) => !prev);
        }}
      >
        <div className="bdr-v2-content">
          {bagState?.afterId && (
            <div className="bdr-v2-section">
              <div className="delete-img-box">
                <ul className="delete-info-ul">
                  <li>Date Completed: {bagState?.dateEntered}</li>
                  <li>Refinishers: {nameListFormat(bagState?.refinishers)}</li>
                  <li>Stain Color: {bagState?.stainColor}</li>
                  <li>Week Display: {bagState?.weekGalleryDisplay}</li>
                </ul>
                <img
                  className="delete-img"
                  src={bagState?.beforeId?.s3ImageUrl ?? baImageUrl(bagState?.beforeId?._id)}
                  alt="before pic"
                />
                <img
                  className="delete-img"
                  src={bagState?.afterId?.s3ImageUrl ?? baImageUrl(bagState?.afterId?._id)}
                  alt="after pic"
                />
              </div>
            </div>
          )}
          <div className="bdr-v2-section">
            <BdrInput
              className="bdr-field--w-95"
              type="text"
              label={"Group Id to Delete"}
              name="GroupId"
              inputRef={deleteBagInputRef}
              onChange={handleGroupIdChange}
              onFocus={clearError}
              onBlur={checkError}
              value={selectedId}
            />
            {/* `danger` is the solid weight — it is the ONLY danger weight, a
                full --bdr-grad-danger fill rather than a tint. It also brings
                the confirm gate this button did not have: it deleted a group
                on a single click once the id validated. */}
            <BdrButton
              variant="danger"
              note="Deleting a B&A group cannot be undone."
              onClick={async () => {
                const isValid = await checkError();
                if (bagState?.dateEntered && isValid) {
                  const deleted = await deleteBAG({
                    variables: {
                      baGroupId: selectedId,
                    },
                  });

                  if (deleted?.data.deleteBAGroup === true) {
                    setBAGState({});
                    setSelectedId("");
                    handleSnack("Group Deleted", 2000);
                  }

                  return;
                }

                setErrorState(true);
              }}
            >
              Delete Group
            </BdrButton>
            <MyFormHelperText
              errorText={"Please enter valid Id"}
              formError={errorState}
            />
          </div>
        </div>
      </BdrDisclosure>
    </div>
  );
};

export default DeleteBAGroup;
